import IG11PointLeadersCore from 'components/Infographics/Cores/IG11_PointLeaders_Core';
import IG12GoalLeadersCore from 'components/Infographics/Cores/IG12_GoalLeaders_Core';
import IG15PenaltyLeadersCore from 'components/Infographics/Cores/IG15_PenaltyLeaders_Core';
import IG2DayRecapCore from 'components/Infographics/Cores/IG2_DayRecap_Core';
import IG5StandingsByPointsCore from 'components/Infographics/Cores/IG5_StandingsByPoints_Core';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useInterval from 'use-interval';
import { parseEmbedUrl } from 'utils/IGParamLogic';

dayjs.extend(utc);
dayjs.extend(timezone);

const CET = 'Europe/Paris';

const EmbedInfographic = () => {
  const location = useLocation();
  const [igId, setIgId] = useState(null);
  const [params, setParams] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      // Parse the current URL to extract infographic ID and parameters
      const currentUrl = window.location.href;
      const { igId: parsedIgId, params: parsedParams } = parseEmbedUrl(currentUrl);
      
      setIgId(parsedIgId);
      setParams(parsedParams);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [location]);

  // Refresh page when date changes in CET (e.g. at midnight for embedded displays)
  const initialDateRef = useRef(dayjs().tz(CET).format('YYYY-MM-DD'));
  useInterval(() => {
    const currentDate = dayjs().tz(CET).format('YYYY-MM-DD');
    if (currentDate !== initialDateRef.current) {
      window.location.reload();
    }
  }, 60_000);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading infographic...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Infographic</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            Please check the URL parameters and try again.
          </p>
        </div>
      </div>
    );
  }

  if (!igId || !params) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="text-gray-600 text-6xl mb-4">📊</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">No Infographic Found</h1>
          <p className="text-gray-600">
            Unable to load the requested infographic.
          </p>
        </div>
      </div>
    );
  }

  // Render the appropriate infographic based on the ID
  const renderInfographic = () => {
    switch (igId) {
      case 'IG2':
        return (
          <IG2DayRecapCore
            exportRef={null}
            metadata={params.metadata || {}}
            size={params.size || { width: 1100, height: 1955 }}
            fgImgPositionX={params.fgImgPositionX ?? 0}
            fgImgPositionY={params.fgImgPositionY ?? 0}
            setFgImgPositionX={() => {}} // No-op for embed
            setFgImgPositionY={() => {}} // No-op for embed
            fgImgZoom={params.fgImgZoom ?? 450}
            setFgImgZoom={() => {}} // No-op for embed
            fgPictureUrl={params.fgPictureUrl ?? ''}
            fgImgMargin={params.fgImgMargin ?? { top: 0, left: 0, bottom: 0, right: 0 }}
            backgroundMode={params.backgroundMode}
            backgroundImgUrl={params.backgroundImgUrl}
            backgroundColor={params.backgroundColor}
            seasonId={params.seasonId}
            date={params.date}
            phaseIdsAll={params.phaseIdsAll}
            venueIdsAll={params.venueIdsAll}
            enablePositioning={false} // Disable positioning for embed
            useImageProxy={false} // Disable image proxy for embed
          />
        );
      case 'IG5':
        return (
          <IG5StandingsByPointsCore
            exportRef={null}
            metadata={params.metadata || {}}
            size={params.size || { width: 625, height: 1111 }}
            fgImgPositionX={params.fgImgPositionX ?? 0}
            fgImgPositionY={params.fgImgPositionY ?? 0}
            setFgImgPositionX={() => {}} // No-op for embed
            setFgImgPositionY={() => {}} // No-op for embed
            fgImgZoom={params.fgImgZoom ?? 100}
            setFgImgZoom={() => {}} // No-op for embed
            fgPictureUrl={params.fgPictureUrl ?? ''}
            fgImgMargin={params.fgImgMargin ?? { top: 0, left: 0, bottom: 0, right: 0 }}
            backgroundMode={params.backgroundMode}
            backgroundImgUrl={params.backgroundImgUrl}
            backgroundColor={params.backgroundColor}
            phaseIdsAll={params.phaseIdsAll}
            enablePositioning={false} // Disable positioning for embed
            useImageProxy={false} // Disable image proxy for embed
            isRolling={params.isRolling || false}
            rollingRowsPerPage={params.rollingRowsPerPage ?? 10}
            rollingIntervalSeconds={params.rollingIntervalSeconds ?? 5}
          />
        );
      case 'IG11':
        return (
          <IG11PointLeadersCore
            exportRef={null}
            metadata={params.metadata || {}}
            size={params.size || { width: 625, height: 1111 }}
            fgImgPositionX={params.fgImgPositionX ?? 0}
            fgImgPositionY={params.fgImgPositionY ?? 0}
            setFgImgPositionX={() => {}} // No-op for embed
            setFgImgPositionY={() => {}} // No-op for embed
            fgImgZoom={params.fgImgZoom ?? 100}
            setFgImgZoom={() => {}} // No-op for embed
            fgPictureUrl={params.fgPictureUrl ?? ''}
            fgImgMargin={params.fgImgMargin ?? { top: 0, left: 0, bottom: 0, right: 0 }}
            backgroundMode={params.backgroundMode}
            backgroundImgUrl={params.backgroundImgUrl}
            backgroundColor={params.backgroundColor}
            phaseIdsAll={params.phaseIdsAll}
            selectedTeams={params.selectedTeams || []}
            topCount={params.topCount}
            enablePositioning={false} // Disable positioning for embed
            useImageProxy={false} // Disable image proxy for embed
          />
        );
      case 'IG12':
        return (
          <IG12GoalLeadersCore
            exportRef={null}
            metadata={dayjs().format('YYYY-MM-DD')}
            size={params.size || { width: 625, height: 1111 }}
            fgImgPositionX={params.fgImgPositionX ?? 0}
            fgImgPositionY={params.fgImgPositionY ?? 0}
            setFgImgPositionX={() => {}} // No-op for embed
            setFgImgPositionY={() => {}} // No-op for embed
            fgImgZoom={params.fgImgZoom ?? 100}
            setFgImgZoom={() => {}} // No-op for embed
            fgPictureUrl={params.fgPictureUrl ?? ''}
            fgImgMargin={params.fgImgMargin ?? { top: 0, left: 0, bottom: 0, right: 0 }}
            backgroundMode={params.backgroundMode}
            backgroundImgUrl={params.backgroundImgUrl}
            backgroundColor={params.backgroundColor}
            phaseIdsAll={params.phaseIdsAll}
            selectedTeams={params.selectedTeams || []}
            enablePositioning={false} // Disable positioning for embed
            useImageProxy={false} // Disable image proxy for embed
          />
        );
      case 'IG15':
        return (
          <IG15PenaltyLeadersCore
            exportRef={null}
            metadata={params.metadata || {}}
            size={params.size || { width: 625, height: 1111 }}
            fgImgPositionX={params.fgImgPositionX ?? 0}
            fgImgPositionY={params.fgImgPositionY ?? 0}
            setFgImgPositionX={() => {}} // No-op for embed
            setFgImgPositionY={() => {}} // No-op for embed
            fgImgZoom={params.fgImgZoom ?? 100}
            setFgImgZoom={() => {}} // No-op for embed
            fgPictureUrl={params.fgPictureUrl ?? ''}
            fgImgMargin={params.fgImgMargin ?? { top: 0, left: 0, bottom: 0, right: 0 }}
            backgroundMode={params.backgroundMode}
            backgroundImgUrl={params.backgroundImgUrl}
            backgroundColor={params.backgroundColor}
            phaseIdsAll={params.phaseIdsAll}
            selectedTeams={params.selectedTeams || []}
            enablePositioning={false} // Disable positioning for embed
            useImageProxy={false} // Disable image proxy for embed
          />
        );
      default:
        return (
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Unsupported Infographic</h1>
            <p className="text-gray-600">
              The infographic type "{igId}" is not supported.
            </p>
          </div>
        );
    }
  };

  return (
    // <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
    //   <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <>{renderInfographic()}</>
    //   </div>
    // </div>
  );
};

export default EmbedInfographic;
